// src/components/FilterHeaderPro.jsx

import React, { useState, useEffect, useContext, useRef } from 'react';
import API from '../api/client';
import { AuthContext } from '../context/AuthContext';
import { TabContext }  from '../context/TabContext';
import './FilterHeaderPro.css';

export default function FilterHeaderPro({
  initialFilters = { species: null, name: '' },
  onApply       = () => {},
  onHide
}) {

  // ── Función de normalización para quitar acentos ─────────────
  const normalizeText = str =>
    str
      .normalize('NFD')               // descompone caracteres acentuados
      .replace(/[\u0300-\u036f]/g, '') // elimina los diacríticos
      .toLowerCase();

  // ───────────────────────────────────────────────────────────────────────────
  // 1) Contexto de usuario para obtener professional_id
  // ───────────────────────────────────────────────────────────────────────────
  const { user } = useContext(AuthContext);
  const { activeTab } = useContext(TabContext);
  const professionalId = user?.professional_id;

  const wrapperRef = useRef(null);

  // ───────────────────────────────────────────────────────────────────────────
  // 2) Cerrar thodo el header al hacer click fuera de él
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    function handleClickOutside(e) {
     // Si hacemos click fuera del wrapper y no es el botón toggle-filter,
     // cerramos el header:
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
       !e.target.closest('.filter-toggle')
      ) {
        onHide();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onHide]);

  // ───────────────────────────────────────────────────────────────────────────
  // 2) Estados locales: especie, nombre, lista completa de nombres y sugerencias
  // ───────────────────────────────────────────────────────────────────────────
  const [localSpecies, setLocalSpecies]   = useState(initialFilters.species);
  const [localName,    setLocalName]      = useState(initialFilters.name);
  const [allNames,     setAllNames]       = useState([]);      // [{id, nombre}, ...]
  const [suggestions,  setSuggestions]    = useState([]);      // subset de allNames
  const [showDropdown, setShowDropdown]   = useState(false);   // para mostrar/ocultar dropdown

  // Ref para la última posición de scroll
  const lastScroll = useRef(0);

  // Ref para click fuera de dropdown
  const dropdownRef = useRef(null);

  // ───────────────────────────────────────────────────────────────────────────
  // 3) Prefetch de nombres al montar
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!professionalId) return;
    API.getPetNames(professionalId)
      .then(resp => setAllNames(resp.data))
      .catch(err => console.error('[FilterHeaderPro] Error cargando nombres:', err));
  }, [professionalId]);

  // ───────────────────────────────────────────────────────────────────────────
  // 4) Filtrar suggestions en memoria
  //    (No abrimos automáticamente el dropdown al montar)
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const text = localName.trim();
    if (text.length === 0) {
      setSuggestions([]);
      return;
    }

    const q = normalizeText(text);

    const matches = allNames
      .filter(p => {
        // Normalizamos también el nombre de cada mascota
        const nameNorm = normalizeText(p.nombre);
        return nameNorm.includes(q);
      })
      .slice(0, 5);
    setSuggestions(matches);
  }, [localName, allNames]);

  // ───────────────────────────────────────────────────────────────────────────
  // 5) Cerrar dropdown al hacer click fuera
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 6) Detectar cambio de pestaña y cerrar
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'prof_pets' && onHide) {
      onHide();
    }
  }, [activeTab, onHide]);

  // ───────────────────────────────────────────────────────────────────────────
  // 7) Detectar scroll para ocultar
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const container = document.querySelector('.pro-container');
    if (!container) return;
    lastScroll.current = container.scrollTop;
    const threshold = 20;
    function handleScroll() {
      const current = container.scrollTop;
      if (current > lastScroll.current + threshold && onHide) {
        onHide();
      }
      lastScroll.current = current;
    }
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [onHide]);

  // ───────────────────────────────────────────────────────────────────────────
  // 8) Select de sugerencia: limpia especie, aplica búsqueda y cierra header
  // ───────────────────────────────────────────────────────────────────────────
  const handleSelectSuggestion = nombre => {
    // Limpiamos cualquier filtro de especie
    setLocalSpecies(null);
    // Fijamos el nombre seleccionado
    setLocalName(nombre);
    // Ocultamos dropdown
    setShowDropdown(false);

    // Ejecutar búsqueda automáticamente
    onApply({ species: null, name: nombre.trim() });
    // Cerrar el header
    if (onHide) onHide();
  };

  // ───────────────────────────────────────────────────────────────────────────
  // 9) Toggles de especie (perro/gato): al cambiar, limpiamos nombre
  // ───────────────────────────────────────────────────────────────────────────
  const toggleSpecies = s => {
    if (localSpecies === s) {
      setLocalSpecies(null);
    } else {
      setLocalSpecies(s);
    }
    // Limpiamos búsqueda de nombre al cambiar especie
    setLocalName('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // 🔟 Render: input con clear-button y lista de sugerencias
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="filter-header-pro" ref={wrapperRef}>
      <div className="filter-left">
        <button
          className={localSpecies === 'perro' ? 'selected' : ''}
          onClick={() => toggleSpecies('perro')}
        >
          Perro
        </button>
        <button
          className={localSpecies === 'gato' ? 'selected' : ''}
          onClick={() => toggleSpecies('gato')}
        >
          Gato
        </button>
      </div>

      <div className="filter-center">
        <input
          type="text"
          className="name-input"
          placeholder="Buscar por nombre…"
          value={localName}
          onChange={e => {
            setLocalName(e.target.value);
            setShowDropdown(true);  // Solo abrimos al tipear
          }}
          onFocus={() => {
            if (suggestions.length > 0) setShowDropdown(true);
          }}
        />
        {/* Botón de limpiar texto */}
        {localName && (
          <button
            className="clear-button"
            onClick={() => {
              setLocalName('');
              setSuggestions([]);
              setShowDropdown(false);
            }}
            aria-label="Limpiar búsqueda"
          >
            ×
          </button>
        )}
        {showDropdown && suggestions.length > 0 && (
          <ul className="suggestions-list">
            {suggestions.map(item => (
              <li
                key={item.id}
                onClick={() => handleSelectSuggestion(item.nombre)}
              >
                {item.nombre}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="filter-right">
        <button
          className="apply-button"
          onClick={() => {
            onApply({ species: localSpecies, name: localName.trim() });
            if (onHide) onHide();
          }}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
}
